import { Preferences } from '@capacitor/preferences';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';
import { environment } from 'environment';
import { GameState } from 'infrastructure/lib/src/models/game-state';
import MainScene from '../Scenes/MainScene';
import { Portal } from 'src/classes/Portal';
import { Inventory } from 'src/classes/UI/Inventory';

/**
 * Read local game state from Preferences and send to server sync endpoint.
 * - Attempts to get Cognito session/token via Amplify Auth
 * - Reads local 'gameState' key from Preferences (expects JSON string)
 *
 * @param apiUrl full URL or path to the sync endpoint (default: '/api/sync-game-state')
 */
export async function syncGameStateWithServer() {
  try {
    // Prefer currentSession for a token
    const session: AuthSession = await fetchAuthSession();
    const auth = `Bearer ${session.tokens.idToken}`;

    // Read local game state from Preferences (inventory, location, visitedPlaces, gpsVisitedPlaces, tokens)
    const inventory = JSON.parse((await Preferences.get({ key: 'inventory' })).value || '{}');
    const playerLocation = JSON.parse((await Preferences.get({ key: 'location' })).value || '{}');
    const visitedPlaces = JSON.parse((await Preferences.get({ key: 'visitedPlaces' })).value || '[]');
    const gpsVisitedPlaces = JSON.parse((await Preferences.get({ key: 'gpsVisitedPlaces' })).value || '[]');
    const tokens = JSON.parse((await Preferences.get({ key: 'tokens' })).value || '[]');
    const lastSyncedAt = (await Preferences.get({ key: 'lastSyncedAt' })).value || new Date(0).toISOString();
    const clientModifiedAt = (await Preferences.get({ key: 'clientModifiedAt' })).value || new Date(0).toISOString();

    const payload: GameState = {
      type: 'GameState',
      clientModifiedAt: clientModifiedAt,
      lastSyncedAt: lastSyncedAt,
      inventory,
      tokens,
      playerLocation,
      visitedPlaces,
      gpsVisitedPlaces,
    };

    // Store clientModifiedAt back to Preferences to ensure it exists in the future
    Preferences.set({
      key: 'clientModifiedAt',
      value: clientModifiedAt
    });

    const resp = await fetch(`${environment.REST_API}sync-game-state`, {
      method: 'POST',
      headers: { 'Content-Type':'application/json', Authorization: auth},
      body: JSON.stringify(payload),
    });

    if (!resp.ok) {
      throw new Error(`Failed to sync game state: ${resp.status} ${resp.statusText}`);
    }

    const result = await resp.json();
    const syncdGameState = result.gameState;
    console.log('syncGameStateWithServer syncdGameState:', syncdGameState);

    // Update local Preferences with the synced game state
    await Preferences.set({ key: 'inventory', value: JSON.stringify(syncdGameState.inventory) });
    await Preferences.set({ key: 'playerLocation', value: JSON.stringify(syncdGameState.playerLocation) });
    await Preferences.set({ key: 'visitedPlaces', value: JSON.stringify(syncdGameState.visitedPlaces) });
    await Preferences.set({ key: 'gpsVisitedPlaces', value: JSON.stringify(syncdGameState.gpsVisitedPlaces) });
    await Preferences.set({ key: 'tokens', value: JSON.stringify(syncdGameState.tokens) });
    await Preferences.set({ key: 'lastSyncedAt', value: syncdGameState.lastSyncedAt || new Date().toISOString() });

    // Update in current game session.
    Inventory.loadInventory();
    MainScene.tokens = syncdGameState.tokens || [];
    Portal.visitedPlaces = syncdGameState.visitedPlaces || [];
    Portal.gpsVisitedPlaces = syncdGameState.gpsVisitedPlaces || [];
    return { ok: true };

  } catch (e) {
    console.error('syncGameStateToServer error', e);
    return { ok: false, error: e };
  }
}
